import React, { useCallback, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { PDFDocument } from 'pdf-lib';
import { withStyles } from '@mui/styles';
import { PropTypes } from 'prop-types';
import {
  Box,
  Button,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import MergeType from '@mui/icons-material/MergeType';
import Download from '@mui/icons-material/Download';
import PictureAsPdf from '@mui/icons-material/PictureAsPdf';

const styles = (theme) => ({
  container: {
    padding: theme.spacing(3),
  },
  intro: {
    marginBottom: theme.spacing(2),
    color: '#555',
  },
  toolTitle: {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  dropzone: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    border: '2px dashed #aaaaaa',
    borderRadius: 5,
    padding: theme.spacing(4),
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: '#fafafa',
  },
  dropzoneActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: '#f0f6ff',
  },
  summaryRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  error: {
    marginTop: theme.spacing(2),
    color: theme.palette.error.main,
  },
  result: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#f0f9f0',
    border: '1px solid #cfe8cf',
    borderRadius: 5,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  emptyPdfIcon: {
    fontSize: 40,
    color: '#bbb',
  },
});

function formatBytes(bytes) {
  if (!bytes) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / (1024 ** i);
  return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

let uid = 0;

function PdfMergeTool({ classes }) {
  // Each entry: { id, file }
  const [files, setFiles] = useState([]);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // { url, name, size }

  const revokeResult = useCallback(() => {
    setResult((prev) => {
      if (prev && prev.url) {
        URL.revokeObjectURL(prev.url);
      }
      return null;
    });
  }, []);

  const onDrop = useCallback((accepted) => {
    const pdfs = accepted.filter(
      (f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name),
    );
    if (pdfs.length === 0) {
      return;
    }
    setError(null);
    revokeResult();
    setFiles((prev) => [
      ...prev,
      ...pdfs.map((file) => {
        uid += 1;
        return { id: uid, file };
      }),
    ]);
  }, [revokeResult]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true,
  });

  const totalSize = useMemo(
    () => files.reduce((sum, f) => sum + f.file.size, 0),
    [files],
  );

  const move = (index, delta) => {
    setFiles((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) {
        return prev;
      }
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    revokeResult();
  };

  const remove = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
    revokeResult();
  };

  const clearAll = () => {
    setFiles([]);
    setError(null);
    revokeResult();
  };

  const merge = async () => {
    setMerging(true);
    setError(null);
    revokeResult();
    try {
      const merged = await PDFDocument.create();
      for (let i = 0; i < files.length; i += 1) {
        const bytes = await files[i].file.arrayBuffer();
        const doc = await PDFDocument.load(bytes, { ignoreEncryption: true });
        const pages = await merged.copyPages(doc, doc.getPageIndices());
        pages.forEach((page) => merged.addPage(page));
      }
      const mergedBytes = await merged.save();
      const blob = new Blob([mergedBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setResult({ url, name: 'merged.pdf', size: blob.size });
    } catch (e) {
      setError(
        'Something went wrong while merging. One of the files may be corrupted or password protected.',
      );
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className={classes.container}>
      <Typography className={classes.toolTitle}>Merge PDFs</Typography>
      <Typography className={classes.intro}>
        Combine multiple PDF files into a single PDF. Add your files, drag them into the
        order you want, then click Merge. Everything happens in your browser &mdash; your
        files are never uploaded to our servers.
      </Typography>

      <div
        {...getRootProps({
          className: `${classes.dropzone} ${isDragActive ? classes.dropzoneActive : ''}`,
        })}
      >
        <input {...getInputProps()} />
        <PictureAsPdf className={classes.emptyPdfIcon} />
        <Typography>
          Drag &amp; drop PDF files here, or click to select files.
        </Typography>
      </div>

      {files.length > 0 && (
        <>
          <Paper variant='outlined'>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 48 }}>#</TableCell>
                  <TableCell>File name</TableCell>
                  <TableCell align='right'>Size</TableCell>
                  <TableCell align='right' style={{ width: 160 }}>Order</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((f, index) => (
                  <TableRow key={f.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{f.file.name}</TableCell>
                    <TableCell align='right'>{formatBytes(f.file.size)}</TableCell>
                    <TableCell align='right'>
                      <Tooltip title='Move up'>
                        <span>
                          <IconButton
                            size='small'
                            disabled={index === 0}
                            onClick={() => move(index, -1)}
                          >
                            <ArrowUpward fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title='Move down'>
                        <span>
                          <IconButton
                            size='small'
                            disabled={index === files.length - 1}
                            onClick={() => move(index, 1)}
                          >
                            <ArrowDownward fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title='Remove'>
                        <IconButton size='small' onClick={() => remove(f.id)}>
                          <DeleteOutline fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <div className={classes.summaryRow}>
            <Typography color='textSecondary'>
              {files.length} file{files.length === 1 ? '' : 's'} &middot;{' '}
              {formatBytes(totalSize)} total
            </Typography>
            <div className={classes.actions}>
              <Button onClick={clearAll} disabled={merging}>
                Clear all
              </Button>
              <Button
                variant='contained'
                color='primary'
                startIcon={<MergeType />}
                onClick={merge}
                disabled={merging || files.length < 2}
              >
                {merging ? 'Merging…' : 'Merge PDFs'}
              </Button>
            </div>
          </div>
          {files.length < 2 && (
            <Typography variant='caption' color='textSecondary'>
              Add at least two PDFs to merge.
            </Typography>
          )}
        </>
      )}

      {error && <Typography className={classes.error}>{error}</Typography>}

      {result && (
        <Box className={classes.result}>
          <Download color='success' />
          <Typography>
            Your merged PDF is ready ({formatBytes(result.size)}):{' '}
            <Link href={result.url} download={result.name}>
              Download {result.name}
            </Link>
          </Typography>
        </Box>
      )}
    </div>
  );
}

PdfMergeTool.propTypes = {
  classes: PropTypes.object.isRequired,
};

const ToolsTab = withStyles(styles)(PdfMergeTool);

export { ToolsTab };
